import type { Dict } from '../types'

/** Danish. Translated best-effort — please proofread (create2stay is Danish-first). */
export const da: Dict = {
  languageName: 'Dansk',

  common: {
    points: 'point',
  },

  // Subject-word lexicon (see en.ts). Danish defaults for clothing (tøj = neuter). A brand supplies
  // the full inflected phrase per form so article agreement is theirs (dit/det for neuter tøj →
  // dine/de for plural smykker/sko).
  //
  // The reward forms are what the Danish copy interpolates everywhere — no string says "værdibevis"
  // any more, so a shop that calls it a gavekort changes these four values and nothing else. Compounds
  // are built in the copy as {reward}kode, which works for a Danish noun but not for a phrase: a brand
  // whose word doesn't compound needs a dictionary override for those two strings.
  subject: {
    noun: 'tøj',
    nounPoss: 'dit tøj',
    nounDef: 'det tøj',
    reward: 'værdibevis',
    rewardPoss: 'dit værdibevis',
    rewardDef: 'værdibeviset',
    rewardPlural: 'værdibeviser',
  },

  auth: {
    signingIn: 'Logger dig ind…',
    stillWorking: 'Arbejder stadig — vi henter dine tidligere ordrer. Det kan tage et øjeblik.',
    loadingItems: 'Henter dine varer…',
    errorTitle: 'Vi kunne ikke starte din trade-in',
    errorGeneric: 'Noget gik galt under login.',
    missingShop: 'Appen er forkert konfigureret — mangler shopId.',
    needSignIn: 'Du skal logge ind, før du kan lave en trade-in.',
    signInFailed: 'Login mislykkedes. Prøv igen.',
    linkInvalid: 'Dette login-link virker ikke længere. Profilen, det hører til, kan være blevet slettet. Gå tilbage til forsiden for at logge ind igen.',
    backToFrontPage: 'Tilbage til forsiden',
  },

  config: {
    title: 'Konfigurationsfejl',
    missingShop: 'Denne trade-in mangler sin shop-konfiguration (shopId). Kontakt venligst butikken.',
  },

  login: {
    title: 'Giv {nounPoss} nyt liv',
    subtitle: 'Log ind for at starte din trade-in og gør {noun}, du ikke længere bruger, til et {reward}.',
    emailLabel: 'E-mail',
    emailPlaceholder: 'dig@eksempel.dk',
    start: 'Fortsæt',
    working: 'Vent venligst…',
    emailSentTitle: 'Tjek din e-mail',
    emailSentBody: 'Vi har sendt et login-link til {email}. Åbn det på denne enhed for at fortsætte.',
    phoneLabel: 'Telefonnummer',
    phonePlaceholder: '12 34 56 78',
    sendCode: 'Send kode',
    codeLabel: 'Bekræftelseskode',
    codePlaceholder: '6-cifret kode',
    verify: 'Bekræft & fortsæt',
    changePhone: 'Brug et andet nummer',
    back: 'Tilbage',
    methodSms: 'Log ind med SMS',
    methodSmsDesc: 'Vi sender dig en kode på sms — hvis vi har registreret dit telefonnummer.',
    methodEmail: 'Log ind med e-mail',
    methodEmailDesc: 'Vi sender et login-link til din indbakke.',
    or: 'eller',
    wholesale: 'Købt i butik eller i gave?',
    emailInvalid: 'Indtast en gyldig e-mailadresse.',
    phoneInvalid: 'Indtast et gyldigt telefonnummer.',
    codeInvalid: 'Koden virkede ikke. Prøv igen.',
    failed: 'Noget gik galt. Prøv igen.',
    step1Title: 'Find dine ting',
    step1Body: 'Søg eller tag et billede af det, du ikke længere bruger.',
    step2Title: 'Send dem ind',
    step2Body: 'Pak det hele i én pakke med din forudbetalte label.',
    step3Title: 'Få {rewardPoss}',
    step3Body: {
      received: 'Modtag {rewardPoss}, når vi har modtaget dine varer.',
      handin: 'Modtag {rewardPoss}, så snart din pakke er scannet på afleveringsstedet.',
      instant: 'Få {rewardPoss} med det samme — og send derefter dine varer til os.',
    },
  },

  country: {
    title: 'Dit land understøttes muligvis ikke',
    body: 'Det ser ud til, at vi måske ikke har en fragtløsning, der kan sende en trade-in fra dit land til vores lager. Du er velkommen til at fortsætte, men vi kan muligvis ikke gennemføre afhentningen.',
    dismiss: 'Fortsæt alligevel',
  },

  menu: {
    open: 'Menu',
    language: 'Sprog',
    myTradeIns: 'Mine trade-ins',
    howItWorks: 'Sådan fungerer det',
    noneYet: 'Ingen endnu',
    logout: 'Log ud',
    soon: 'Snart',
  },

  // Værdibevis-rækker, genbrugt af "Mine trade-ins"-siden til værdibevis-sektionen.
  rewards: {
    activeHeading: 'Tilgængelige',
    spentHeading: 'Brugte',
    expires: 'Udløber {date}',
    active: 'Aktiv',
    spentBadge: 'Brugt',
  },
  // "Mine trade-ins"-kontoside: værdibeviser først, derefter trade-in-historik + detaljedialog.
  myTradeIns: {
    title: 'Mine trade-ins',
    subtitle: 'Dine trade-ins og {rewardPlural}.',
    back: 'Tilbage til trade-in',
    loading: 'Henter dine trade-ins…',
    empty: 'Du har ingen trade-ins eller {rewardPlural} endnu.',
    historyHeading: 'Dine trade-ins',
    tradeInPrefix: 'Trade-in',
    itemsCount: '{count} varer',
    statusReceived: 'Modtaget',
    statusOnItsWay: 'På vej til os',
    statusAwaiting: 'Afventer din pakke',
    detailTitle: 'Trade-in-detaljer',
    nextHeading: 'Hvad sker der nu',
    nextReceived: 'Vi har modtaget dine varer — {rewardPoss} er på vej.',
    nextTransit: 'Din pakke er på vej til os. Vi sender dig en e-mail, så snart vi har modtaget og tjekket dine varer.',
    nextPickup: 'Tag dine varer med i vores butik, så klarer vi resten.',
    nextShip: 'Pak dine varer og send dem til os med oplysningerne nedenfor.',
    itemsHeading: 'Varer i denne trade-in',
    labelUnavailable: 'Fragtlabelen er ikke længere tilgængelig.',
    close: 'Luk',
  },

  steps: {
    items: 'Varer',
    story: 'Din historie',
    details: 'Detaljer',
    shipping: 'Forsendelse',
    confirm: 'Bekræft',
  },

  rail: {
    title: 'Din trade-in',
    itemCount: '{count} vare',
    itemCountPlural: '{count} varer',
    empty: 'Ingen varer endnu — tilføj din første, og se værdien vokse.',
    loading: 'Henter din trade-in…',
    estimatedReward: 'Samlet værdi',
    rewardNoteConfirmed: {
      received: 'Bekræftes, når vi modtager dine varer',
      handin: 'Bekræftes, når du afleverer din pakke',
      instant: 'Udstedes, så snart du bekræfter din trade-in',
    },
    rewardNoteAdd: 'Tilføj varer for at se værdien',
    addedManually: 'Tilføjet manuelt',
    maxExceeded: 'Denne trade-in overstiger maksimum på {amount}. Fjern en vare for at fortsætte.',
    remove: 'Fjern {name}',
    back: 'Tilbage',
    continue: 'Fortsæt',
    confirmTradeIn: 'Bekræft trade-in',
    done: 'Færdig',
  },

  items: {
    firstTitle: 'Lad os finde din første vare',
    firstSubtitle: 'Tag et billede, eller søg i kataloget.',
    anotherTitle: 'Tilføj endnu en vare?',
    anotherSubtitle: 'Gennemgå det hele, før du fortsætter.',
    selectTitle: 'Vælg varer til trade-in',
    selectSubtitle: 'Fra dine tidligere ordrer — tryk for at tilføje.',
    manualTitle: 'Tilføj en vare manuelt',
    manualSubtitle: 'Til varer købt andetsteds.',
    estReward: 'Værdi',
    bought: 'Købt {date}',
    addManually: 'Tilføj manuelt',
    boughtElsewhere: 'Købt andetsteds?',
    addedCount: '{count} tilføjet',
    nothingTitle: 'Ingen varer at bytte ind',
    nothingBody:
      'Vi kunne ikke finde nogen varer på din konto, der kan byttes ind. Trade-in her gælder kun for varer købt hos os — så snart du har en berettiget ordre, vises den her.',
  },

  intro: {
    title: 'Sådan fungerer trade-in',
    lead: 'Byt {noun} fra {brand}, som du ikke længere bruger, og få {rewardPoss} til nyt eller brugt.',
    step1Title: 'Find dine ting',
    step1Body: 'Tag et billede af det, du ikke længere bruger, eller søg i vores katalog. Tilføj lige så mange ting, du vil.',
    step2Title: 'Send dem til os',
    step2Body: 'Pak det hele i én pakke med din forudbetalte label — fragten betaler vi.',
    step3Title: 'Få {rewardPoss}',
    step3Body: {
      received: 'Når vi har modtaget og tjekket dine ting, sender vi {rewardPoss} på mail.',
      handin: 'Så snart din pakke er scannet ved afleveringen, sender vi {rewardPoss} på mail.',
      instant: '{rewardPoss|cap} sendes på mail, så snart du bekræfter — derefter sender du dine ting til os.',
    },
    cta: 'Forstået',
    close: 'Luk',
  },

  finder: {
    search: 'Søg i katalog',
    photo: 'Tilføj med billede',
    searchTitle: 'Søg i kataloget',
    searchSub: 'Efter SKU, EAN eller produktnavn.',
    searchPlaceholder: 'Søg på SKU, EAN eller produktnavn',
    carelabelHint: 'Tip: find SKU- eller stylenummeret på varens vaskeanvisning.',
    searching: 'Søger…',
    searchFailed: 'Søgningen mislykkedes — prøv igen.',
    noMatches: 'Ingen resultater for “{query}”.',
    unavailable: 'Katalogsøgning er ikke tilgængelig for denne shop.',
    estReward: 'Værdi',
    notEligible: 'Ikke berettiget',
    add: 'Tilføj',
    adding: 'Tilføjer…',
    added: '{name} tilføjet · +{amount}',
    backToOrders: 'Tilbage til dine ordrer',
    showMore: 'Vis flere resultater',
    loadingMore: 'Henter…',
  },

  photo: {
    dropTitle: 'Tag et billede af din vare',
    dropHint: 'Så finder vi lignende varer i kataloget.',
    or: 'eller',
    wayDevice: 'Tag eller upload et billede',
    wayPhone: 'Brug din telefons kamera',
    qrAgainTitle: 'Prøv et andet billede',
    qrAgainHint: 'Tag et nyt billede på din telefon, så søger vi automatisk igen.',
    analysingTitle: 'Finder dine bedste match…',
    analysingHint: 'Sammenligner dit billede med vores katalog',
    uploadingTitle: 'Uploader dit foto…',
    uploadingHint: 'Et øjeblik',
    yourPhoto: 'Dit billede',
    matchedTo: 'Matchet med vores katalog',
    replace: 'Erstat',
    headingConfident: 'Ligner en af disse din vare?',
    headingCategory: 'Tætteste match i {category}',
    noMatchHeading: 'Vi fandt ikke et tæt match',
    noMatchBody:
      'Ingen af vores katalogvarer matchede tæt. Prøv et andet billede, eller søg på navn i stedet.',
    // Distinct from "no match": the search never ran, so the item may well be in the catalogue.
    notSearched: 'Ikke søgt endnu',
    searchFailedHeading: 'Vi kunne ikke søge lige nu',
    searchFailedBody:
      'Noget gik galt hos os, så vi har ikke sammenlignet dit billede endnu. Dit billede er gemt — prøv venligst igen.',
    tryAgain: 'Prøv igen',
    refine: 'Filtrér efter kategori',
    clear: 'Ryd',
    noneOfThese: 'Ingen af disse',
    // "Tilføj efter kategori" — fallback fra pladsholder-feltet i fotoresultaterne.
    categoryCardTitle: 'Kan du ikke finde din vare?',
    categoryCardHint: 'Vælg en kategori i stedet',
    categoryPickTitle: 'Hvilken slags vare er det?',
    categoryPickHint: 'Vi bruger det foto, du lige har taget.',
    categoryValue: 'Værdi',
    categoryConfirm: 'Tilføj vare',
    categoryBack: 'Tilbage',
    uploadAnother: 'Upload et andet billede',
    bestMatch: 'Bedste match',
    uploadFailed: 'Noget gik galt — prøv igen.',
  },

  match: {
    strong: 'Stærkt match',
    colour: 'Farve: {color}',
    estimatedReward: 'Værdi',
    backToMatches: 'Tilbage til match',
    backToResults: 'Tilbage til resultater',
    confirm: 'Ja, det er min vare',
  },

  details: {
    title: 'Dine oplysninger',
    subtitle: 'Så vi kan oprette din forudbetalte fragtlabel og sende {rewardPoss}.',
    contact: 'Kontakt',
    address: 'Adresse',
    fullName: 'Fulde navn',
    email: 'E-mail',
    country: 'Land',
    streetAddress: 'Vejadresse',
    postalCode: 'Postnummer',
    city: 'By',
    prefix: 'Landekode',
    phone: 'Telefon',
    emailLocked: 'Din e-mail er knyttet til din konto og kan ikke ændres her.',
    consentPre: 'Jeg accepterer ',
    consentLink: 'trade-in vilkår & betingelser',
    consentPost: ' og bekræfter, at varerne er mine at bytte ind.',
    required: 'Dette felt er påkrævet.',
    emailInvalid: 'Indtast en gyldig e-mailadresse.',
    phoneInvalid: 'Indtast et gyldigt telefonnummer.',
    zipInvalid: 'Indtast et gyldigt postnummer',
    zipFormat: 'format: {format}',
    loading: 'Henter dine oplysninger…',
    saveFailed: 'Vi kunne ikke gemme dine oplysninger. Prøv igen.',
  },

  terms: {
    title: 'Vilkår og betingelser for indlevering af {noun}',
    // {brand} er butikkens brandnavn (config.brandName), eller en generisk fallback hvis det mangler.
    introLead:
      'Som led i vores secondhand-initiativ kan du ombytte {nounPoss} fra {brand} til et {reward}.',
    introAccept:
      'Ved at indsende {nounPoss} fra {brand} accepterer du, at der kun udstedes {reward} for {noun}, der lever op til følgende betingelser, og at {noun}, der ikke lever op til kravene, IKKE sendes retur til dig.',
    introDonate:
      '{noun|cap}, der ikke lever op til vores krav, bliver doneret til velgørenhed eller givet til virksomheder, som kan genbruge {nounDef} eller materialer fra {nounDef}.',
    requirementsTitle: 'Krav til {nounDef}, du indleverer',
    requirementsBody:
      'Vi modtager {noun} fra {brand}, som er rent, og hvor alle originale mærker er intakte. Det gælder både necklabel og carelabel (vaskeanvisning m.v.).',
    howToTitle: 'Sådan indleverer du {nounPoss}',
    howToRegister:
      'Du registrerer de {brand}-produkter, som du ønsker at indlevere. I forbindelse med registreringen kan du se værdien for det pågældende produkt. Værdien er det beløb, du vil modtage som {reward} i bytte for {nounPoss}.',
    howToAdjust:
      'Hvis det, vi modtager, ikke svarer til det, du har registreret, retter vi det til det korrekte produkt og justerer din samlede værdi tilsvarende. Ved at indsende {nounPoss} accepterer du, at vi foretager denne justering.',
    howToLabel:
      'Når du har færdiggjort din registrering, modtager du en fragtlabel eller QR-kode, som du skal bruge til at indsende {nounPoss}. Denne fragtlabel er forudbetalt af {brand}.',
    howToFinal:
      'Når du har indleveret {nounPoss}, kan du ikke fortryde din indlevering, og vi sender ikke dine produkter retur.',
    voucherTitle: '{reward|cap}',
    voucherIssue: {
      received:
        'Du modtager et {reward}, når vi har bekræftet, at {nounPoss} lever op til ovenstående krav. {rewardDef|cap} sendes til den oplyste e-mailadresse.',
      handin:
        'Du modtager et {reward}, når din pakke er registreret på afleveringsstedet. {rewardDef|cap} sendes til den oplyste e-mailadresse. Hvis {nounPoss} ikke lever op til ovenstående krav, kan {rewardDef} trækkes tilbage.',
      instant:
        'Du modtager et {reward}, så snart du registrerer din trade-in. {rewardDef|cap} sendes til den oplyste e-mailadresse. Hvis {nounPoss} ikke sendes til os eller ikke lever op til ovenstående krav, kan {rewardDef} trækkes tilbage.',
    },
    voucherValidity:
      '{rewardDef|cap} er gyldigt i 3 år fra udstedelsen og kan anvendes til køb hos {brand} online. {rewardDef|cap} kan ikke ombyttes til kontanter.',
    closing: 'Vi glæder os til at modtage {nounPoss}.',
    brandFallback: 'brandet',
    close: 'Luk',
    accept: 'Accepter & fortsæt',
  },

  shipping: {
    title: 'Hvordan sender du det ind?',
    subtitle: 'Pak dine {count} vare — én label til det hele.',
    subtitlePlural: 'Pak dine {count} varer — én label til det hele.',
    loading: 'Henter fragtmuligheder…',
    none: 'Der er endnu ingen fragtløsning til din adresse.',
    maxExceeded: 'Denne trade-in overstiger den maksimale værdi. Fjern en vare, og prøv igen.',
    createFailed: 'Vi kunne ikke oprette din trade-in. Prøv igen.',
    desc_gls: 'Aflever i en GLS pakkeshop. Gratis, printet label.',
    desc_dhl: 'Aflever i en DHL pakkeshop. Gratis, printet label.',
    desc_dao: 'En kurér henter ved din dør.',
    desc_shipmondo: 'Aflever med en printet label.',
    desc_pickup: 'Aflever i vores fysiske butik.',
    desc_postnord: 'Aflever i et PostNord pakkeudleveringssted.',
  },

  story: {
    title: 'Fortæl os din historie',
    subtitle: 'Valgfrit — vi elsker at høre om dine varer.',
    placeholder: 'Fortæl om denne vare — hvor du bar den, hvorfor du holdt af den…',
    consent:
      'I må dele min historie (anonymt) for at inspirere andre kunder til at give deres {noun} et nyt liv.',
  },

  confirm: {
    topbarTitle: 'Bekræft',
    heroTitle: 'Så er du klar',
    heroBody: {
      received: 'Din trade-in er registreret. Følg de tre trin nedenfor, og {rewardPoss} er på vej.',
      handin:
        'Din trade-in er registreret. Følg de tre trin nedenfor — {rewardPoss} udstedes, så snart du afleverer din pakke.',
      instant:
        'Din trade-in er registreret, og {rewardPoss} er på vej. Følg blot de tre trin nedenfor for at sende dine varer til os.',
    },
    packTitle: 'Pak din {count} vare',
    packTitlePlural: 'Pak dine {count} varer',
    packBody: 'Læg det hele i én pakke. Ingen speciel emballage nødvendig — genbrug en kasse eller pose.',
    pickupPackBody: 'Pak dine varer og tag dem med i vores fysiske butik — ingen fragtlabel nødvendig.',
    shippingCode: 'Forsendelseskode',
    generatingLabel: 'Opretter din fragtlabel…',
    labelFailed: 'Vi kunne ikke oprette din label. Kontakt venligst support.',
    showQr: 'Vis QR-kode',
    showLabel: 'Vis fragtlabel',
    dropTitle: 'Aflever hos en pakkeshop',
    dropBody: 'Vis koden eller labelen på fragtfirmaets afleveringssted. ',
    findShop: 'Find nærmeste pakkeshop →',
    pickupTitle: 'Aflever i butik',
    pickupBody: 'Tag dine varer med i vores butik, så klarer vi resten.',
    rewardTitle: 'Få {rewardPoss}',
    // Split around the amount, which ConfirmPage renders in <b> between the two halves ("…sender vi
    // dit [500 kr.] {reward} på e-mail"). Note the determiner "dit" sits in this half, so unlike every
    // other reward string it does NOT follow the brand's word: a shop whose reward is common-gender
    // ("din tilgodeseddel") has to override these three lines too. Neuter words (gavekort,
    // værdibevis) are unaffected.
    rewardBodyPre: {
      received: 'Når vi modtager din pakke, sender vi dit ',
      handin: 'Så snart din pakke er scannet ved afleveringen, sender vi dit ',
      instant: 'Dit ',
    },
    rewardBodyPost: {
      received: ' {reward} på e-mail inden for 2 hverdage.',
      handin: ' {reward} på e-mail. Giv det venligst et par timer, før afleveringens sporingsdata når frem til os.',
      instant: ' {reward} er allerede på vej til din indbakke — send blot dine varer til os via trinene ovenfor.',
    },
    // INSTANT-strategi: værdibeviskoden vises her, så snart Core har udstedt den.
    voucherReadyTitle: '{rewardPoss|cap} er klar',
    voucherIssuing: 'Opretter {rewardPoss}…',
    voucherExpires: 'udløber {date}',
    copyCode: 'Kopiér {reward}kode',
    codeCopied: '{reward|cap}kode kopieret',
    startAnother: 'Start en ny trade-in',
    backToShop: 'Tilbage til shoppen',
  },

  dropoff: {
    title: 'Find nærmeste pakkeshop',
    qrSupported: 'Understøtter QR-aflevering — ingen printet label nødvendig.',
    qrNotSupported: 'Kræver en printet label.',
  },
}
